import ApiRequest from "@/common/lib/apiRequest";
import gnosis from "./gnosis.module";

const mutationTypes = {
  GET_WALLETS: "staking/GET_WALLETS",
  SET_WALLETS: "staking/SET_WALLETS",
  CREATE_WALLET: "staking/CREATE_WALLET",
  SET_CREATE_WALLET_RESPONSE: "staking/SET_CREATE_WALLET_RESPONSE",
  SET_CURRENT_WALLET: "staking/SET_CURRENT_WALLET",
  SET_SECONDARY_WALLET: "staking/SET_SECONDARY_WALLET",
  UPDATE_WALLET: "staking/UPDATE_WALLET",
  DELETE_WALLET: "staking/DELETE_WALLET",
};

const state = {
  walletList: {
    fetching: false,
    result: null,
    error: null,
  },
  walletCreation: {
    isSubmitting: false,
    isSubmitted: false,
    error: null,
  },
  walletSelected: {},
  secondaryWalletSelected: {},
};

const actions = {
  async fetchWallets({ commit }) {
    commit(mutationTypes.GET_WALLETS);
    try {
      let result = await ApiRequest.get("/account");
      result = result.sort((a, b) => b.id - a.id);
      commit(mutationTypes.SET_WALLETS, { result });
    } catch (error) {
      commit(mutationTypes.SET_WALLETS, { error });
    }
  },
  async addWallet({ commit, dispatch, rootGetters }, payload) {
    commit(mutationTypes.CREATE_WALLET);
    payload = { ...payload };
    try {
      if (!payload.assetId) {
        payload.assetId = rootGetters["appConfig/assetList"].find(
          c => c.network === payload.network
        ).assetId;
      }
      await ApiRequest.post("/account/create", {
        asset_id: payload.assetId,
        label: payload.accountLabel,
        address: payload.address,
        type: payload.accountType || undefined,
        metadata: payload.metadata,
      });
      commit(mutationTypes.SET_CREATE_WALLET_RESPONSE, {
        isSubmitting: false,
        isSubmitted: true,
      });
      await dispatch("fetchWallets");
    } catch (error) {
      commit(mutationTypes.SET_CREATE_WALLET_RESPONSE, {
        error,
        isSubmitting: false,
        isSubmitted: true,
      });
    }
  },
  async updateWallet({ commit }, payload) {
    commit(mutationTypes.UPDATE_WALLET, payload);
    return ApiRequest.patch("/account/" + payload.id, {
      label: payload.label,
      is_pinned: payload.isPinned,
      is_selected: payload.isSelected,
      metadata: payload.metadata || {},
    });
  },
  async deleteWallet({ commit, getters, dispatch }, walletId) {
    await ApiRequest.delete("/account/" + walletId);
    commit(mutationTypes.DELETE_WALLET, walletId);
    if (getters.walletSelected?.id === walletId) {
      commit(mutationTypes.SET_CURRENT_WALLET, {});
    }
  },
  async disconnectSecondWallet({ commit, getters }, walletId) {
    await ApiRequest.delete("/account/" + walletId);
    commit(mutationTypes.DELETE_WALLET, walletId);
    commit(mutationTypes.SET_SECONDARY_WALLET, {});
  },
  setCurrentWallet({ commit }, wallet) {
    commit(mutationTypes.SET_CURRENT_WALLET, wallet);
  },
  setSecondaryWallet({ commit }, wallet) {
    commit(mutationTypes.SET_SECONDARY_WALLET, wallet);
  },
  async fetchOtherInfo({ commit, state }) {
    const wallets = state.walletList.result;
    if (!wallets || !wallets.length) return;
    const aum = await ApiRequest.get("/account/total_aum");
    aum.forEach(aumItem => {
      const wallet = wallets.find(
        w => w.address.toLowerCase() === aumItem.address.toLowerCase()
      );
      if (wallet) {
        wallet.products = [];
        if (aumItem.network === "ethereum") {
          if (aumItem.uvaultStaking.some(item => item.expectedReturn > 0)) {
            wallet.products.push("vaults");
          }
          if (
            [
              "celerStaking",
              "kyberDAOStaking",
              "skaleStaking",
              "thegraphStaking",
            ].some(key => aumItem[key].value > 0)
          ) {
            wallet.products.push("stake");
          }
        } else {
          wallet.products = ["stake"];
        }
      }
    });
    commit(mutationTypes.SET_WALLETS, { result: wallets });
  },
};

const mutations = {
  [mutationTypes.GET_WALLETS](state) {
    state.walletList.fetching = true;
    state.walletList.error = null;
  },
  [mutationTypes.SET_WALLETS](state, { result, error }) {
    state.walletList = {
      fetching: false,
      result,
      error,
    };
  },
  [mutationTypes.CREATE_WALLET](state) {
    state.walletCreation = {
      isSubmitting: false,
      isSubmitted: false,
      error: null,
    };
  },
  [mutationTypes.SET_CREATE_WALLET_RESPONSE](state, data) {
    state.walletCreation = {
      ...state.walletCreation,
      ...data,
    };
  },
  [mutationTypes.SET_CURRENT_WALLET](state, wallet) {
    state.walletSelected = wallet;
  },
  [mutationTypes.SET_SECONDARY_WALLET](state, wallet) {
    state.secondaryWalletSelected = wallet;
  },
  [mutationTypes.UPDATE_WALLET](state, wallet) {
    state.walletList.result = state.walletList.result.map(w => {
      if (w.id !== wallet.id) return w;
      return {
        ...w,
        ...wallet,
      };
    });
  },
  [mutationTypes.DELETE_WALLET](state, walletId) {
    state.walletList.result = state.walletList.result.filter(
      w => w.id !== walletId
    );
  },
};

const getters = {
  walletList: state => state.walletList,
  walletCreation: state => state.walletCreation,
  walletSelected: state => state.walletSelected,
  secondaryWalletSelected: state => state.secondaryWalletSelected,
  signableWallet: (state, getters, rootState, rootGetters) =>
    state.walletSelected &&
    rootGetters["projectConfig/latestProject"] &&
    state.walletSelected.coin_network === rootGetters["projectConfig/latestProject"].config.network
      ? state.walletSelected
      : undefined,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
  modules: {
    gnosis,
  },
};
